import { NavLink } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  Database,
  LogOut,
  Radar,
  Settings,
  ShieldCheck,
  Target,
  Users,
  Workflow,
} from 'lucide-react';
import { toast } from 'sonner';
import type { ComponentType } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/auth-store';

import { SessionStatus } from './SessionStatus';

interface NavItem {
  name: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
}

const navGroups: Array<{ label: string; items: NavItem[] }> = [
  {
    label: 'Core',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: Radar },
      { name: 'Executive', href: '/executive', icon: BarChart3 },
      { name: 'Monitoring', href: '/monitoring', icon: Activity },
    ],
  },
  {
    label: 'Data Ops',
    items: [
      { name: 'Data Sources', href: '/data-sources', icon: Database },
      { name: 'Pipelines', href: '/pipelines', icon: Workflow },
    ],
  },
  {
    label: 'Data Publik',
    items: [
      {
        name: 'IKU Perangkat Daerah',
        href: '/public-datasets/iku-perangkat-daerah',
        icon: Target,
      },
    ],
  },
  {
    label: 'Control',
    items: [{ name: 'Settings', href: '/settings', icon: Settings }],
  },
];

export default function Sidebar() {
  const { user, logout } = useAuthStore();

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  return (
    <aside className="hidden h-screen w-[280px] border-r bg-surface-elevated/90 backdrop-blur md:flex md:flex-col">
      <div className="flex h-20 items-center gap-3 border-b px-5">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-panel">
          <Database className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="font-display text-base font-semibold tracking-tight">DW Command</p>
          <p className="truncate text-xs text-muted-foreground">Kaltara Data Operations</p>
        </div>
      </div>

      <nav role="navigation" className="flex-1 space-y-6 overflow-y-auto px-4 py-5">
        {navGroups.map((group) => (
          <div key={group.label} className="space-y-2">
            <p className="px-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/85">
              {group.label}
            </p>
            <div className="space-y-1">
              {group.items.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                      'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface-elevated',
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-focus'
                        : 'text-muted-foreground hover:bg-surface-focus hover:text-foreground',
                    )
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{item.name}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}

        {user?.role === 'ADMIN' && (
          <div className="space-y-2">
            <p className="px-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/85">
              Admin
            </p>
            <div className="space-y-1">
              <NavLink
                to="/admin/users"
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface-elevated',
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-focus'
                      : 'text-muted-foreground hover:bg-surface-focus hover:text-foreground',
                  )
                }
              >
                <Users className="h-4 w-4 shrink-0" />
                <span className="truncate">User Management</span>
              </NavLink>
              <NavLink
                to="/bnba-datasets"
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface-elevated',
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-focus'
                      : 'text-muted-foreground hover:bg-surface-focus hover:text-foreground',
                  )
                }
              >
                <ShieldCheck className="h-4 w-4 shrink-0" />
                <span className="truncate">Dataset Kustom BNBA</span>
              </NavLink>
            </div>
          </div>
        )}
      </nav>

      <div className="space-y-3 border-t px-4 py-4">
        <SessionStatus />

        <div className="flex items-center gap-3 rounded-lg border bg-surface-base/70 px-3 py-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
            {(user?.username?.charAt(0) || user?.email?.charAt(0) || 'U').toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{user?.username || user?.email || 'User'}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.email || ''}</p>
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="w-full border-border/70 bg-transparent hover:bg-surface-focus"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4 mr-2" />
          Logout
        </Button>
      </div>
    </aside>
  );
}
